import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';

import { InvalidPage } from '../components/errors/invalidPage';
import { CorePerformanceDevices } from '../containers/core/performance/compositions/corePerformanceEventDevices';
import { CorePerformanceUtilizationDevices } from '../containers/core/performance/compositions/corePerformanceUtilizationDevices';
import { CorePerformanceDataUsageDevices } from '../containers/core/performance/compositions/corePerformanceDataUsageDevices';
import { useWorldRoutes } from './parts/allWorldRoutes';
import { usePathBasedQueryString } from '@lib/usePathBasedQueryString';
import { decodeNullableQueryParam } from '@lib/nullableQueryParam';
import { DeviceEventCountName, deviceEventNames } from '../services/core/eventsTypes';
import { DataUsageDeviceType, dataUsageDeviceTypes } from '../services/core/performanceDataUsageDevices';

const performanceEventCountNames: DeviceEventCountName[] = ['deviceRebooted', 'deviceLowPower', 'timeError', 'batteryChanged', 'deviceDropped', 'simChanged'];

const UTILIZATION_NAME = 'utilisation';

const isPerformanceEventCountName = (name: string): name is DeviceEventCountName => {
  return deviceEventNames.includes(name as any) && performanceEventCountNames.includes(name as DeviceEventCountName);
};

const isDataUsageDeviceType = (name: string): name is DataUsageDeviceType => {
  return dataUsageDeviceTypes.includes(name as DataUsageDeviceType);
};

const parseUtc = (utc: string | null): number | null => {
  const parsed = utc ? parseInt(utc, 10) : NaN;
  return isNaN(parsed) ? null : parsed;
};

export const CorePerformanceDevicesRoute = () => {
  const { queryString: search } = usePathBasedQueryString(/\/performance\/devices/);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const name = searchParams.get('name');
  const utc = searchParams.get('utc');
  const homeLocationId = decodeNullableQueryParam(searchParams.get('homeLocationId'));
  const group = decodeNullableQueryParam(searchParams.get('group'));
  const zoneId = searchParams.get('zoneId');
  const model = decodeNullableQueryParam(searchParams.get('model'));
  const ts = searchParams.get('ts');
  const routes = useWorldRoutes();
  const { t } = useTranslation('redirect');

  const invalidPage = <InvalidPage
    message={t('PERFORMANCE_EVENT_NOT_FOUND')}
    redirect={{
      message: t('REDIRECT_TO_PERFORMANCE'),
      path: routes.core.performance
    }}
  />;

  const parsedUtc = parseUtc(utc);

  if (!name || parsedUtc === null) {
    return invalidPage;
  }

  if (name === UTILIZATION_NAME) {
    return (
      <CorePerformanceUtilizationDevices
        key={ts}
        utc={parsedUtc}
        homeLocationId={homeLocationId}
        group={group}
        zoneId={zoneId}
        model={model}
      />
    );
  }

  if (isDataUsageDeviceType(name)) {
    return (
      <CorePerformanceDataUsageDevices
        key={ts}
        type={name}
        utc={parsedUtc}
        homeLocationId={homeLocationId}
        group={group}
        zoneId={zoneId}
        model={model}
      />
    );
  }

  if (!isPerformanceEventCountName(name)) {
    return invalidPage;
  }

  return (
    <CorePerformanceDevices
      key={ts}
      name={name}
      utc={parsedUtc}
      homeLocationId={homeLocationId}
      group={group}
      zoneId={zoneId}
      model={model}
    />
  );
};
